/**
 * Top of the People tab for residents (screenshots/21) and lobbyists / advocacy (23), §2.6 and the §2.9
 * equal-size rule (one implementation; replaces the starter's GroupOverviews sketch, wired to the API).
 *
 * Resident (21): "Your alderperson" card with how they acted on what you
 * follow, and "You vs. your alderperson" (both on one leaning meter; the resident's own estimate comes from
 * their verified profile's public comments). Interactions (screenshots/24): Find my alderperson (card 4,
 * address → ward), Where do you stand? (card 5, private stances).
 * Equal-size rule (§2.9): the panel is 3 equal columns, 224px tall, with matching eyebrow labels; neighbor cards
 * are a fixed 96px. Neighbors' cards open the comment inside its meeting (residents never get a resident's profile).
 *
 * Lobbyist / advocacy (23): your team's positions (owner/admin edit), decision makers on
 * your issues (why they matter, alignment with you, Persuadable flag, + Target), the team's target list (drag to
 * prioritise; To contact / Scheduled / Met), Brief me, and the coalition finder.
 * Equal-size rule (§2.9): decision-maker rows are 64px with the table footer pinned to the bottom; the two
 * side cards are equal (290px) so the columns end level.
 * Interactions (screenshots/24 cards 10–12): add to target list, Brief me (AI one-page brief), and re-rank in
 * place when a team position changes (rows FLIP to their new rank, numbers roll).
 */
import { memo, useEffect, useLayoutEffect, useRef, useState } from "react";
import { ArrowRight, Building2, Check, FileText, Film, GripVertical, Lock, MapPin, Pencil, Plus, Printer, ScrollText, Share2, Sparkles, Tag, ThumbsDown, ThumbsUp, UserRound, Users, X } from "lucide-react";
import { Dropdown } from "../components/shared/Dropdown";
import { Face } from "../components/shared/Face";
import { List, ListRow, RowCell, RowMain, RowSub, RowTitle } from "../components/shared/List";
import { NoWard } from "../components/shared/States";
import { CountBadge } from "../components/shared/ui";
import { flipFrom, recipes, prefersReducedMotion } from "../motion";
import { clock } from "../lib/format";
import { peopleApi, peopleStore, usePeople } from "./peopleStore";
import { LeaningMeter } from "./LeaningMeter";
import { StanceChip, Watch, mdy, meetingHref, profileHref } from "./atoms";
import { leaningToPct } from "./types";
import type { AdvocacyTop as AdvTop, DecisionMaker, ResidentTop as ResTop, Target, TargetStatus, TeamPosition } from "./model";
import { useFocusTrap } from "../hooks/useFocusTrap";
import { reloadPage } from "../app/router";
import { useBusy } from "../hooks/useBusy";
import { viewerRole } from "../nav/navConfig";
import { BriefLimitCard } from "../plans/LimitCards";
import { briefsReached, limitAnswer, type BriefAllowance } from "../plans/planLimits";

/* ---------------- Resident (21) ---------------- */

export function ResidentOverview() {
  const top = usePeople((s) => s.resident);
  const [finding, setFinding] = useState(false);
  if (!top) return null;
  const a = top.alderperson;
  return (
    <>
    <section className="p-yours" aria-label="Your alderperson">
      <div className="p-yours__who">
        {a ? <>
          {a.image.kind === "portrait" ? <img className="p-yours__portrait" src={a.image.url} alt={a.name} /> : <Face name={a.name} size={62} />}
          <div className="p-yours__id">
            <span className="p-eyebrow">YOUR ALDERPERSON · WARD {top.ward}</span>
            <a className="p-yours__name" href={profileHref(a.id)}>{a.name}</a>
            {top.following && <span className="p-following"><Check size={11} aria-hidden />Following</span>}
            <button className="p-link" onClick={() => setFinding((v) => !v)}>Not your ward? Find yours by address</button>
          </div>
        </> : !finding && <NoWard onFind={() => setFinding(true)} />}
        {finding && <FindWard onDone={() => setFinding(false)} />}
      </div>
      {a && <Acted top={top} />}
      {a && <YouVs top={top} />}
    </section>
    {top.ward != null && top.neighbors.total > 0 && <Neighbors top={top} />}
    </>
  );
}

function Neighbors({ top }: { top: ResTop }) {
  const [all, setAll] = useState(false);
  const cards = all ? top.neighbors.cards : top.neighbors.cards.slice(0, 3);
  return (
    <section className="p-neighbors" aria-label={`Neighbors who spoke from Ward ${top.ward}`}>
      <div className="p-sec-head">
        <h2>Neighbors who spoke from Ward {top.ward}</h2><span className="count-badge">{top.neighbors.total}</span>
        {!all && top.neighbors.total > 3 && <button className="link-arrow p-sec-head__right" onClick={() => setAll(true)}>See all {top.neighbors.total}<ArrowRight size={14} aria-hidden /></button>}
      </div>
      <div className="p-neighbors__grid">
        {cards.map((n) => (
          <a key={n.id} className="p-neighbor" href={n.personId ? `/dashboard/people/${encodeURIComponent(n.personId)}` : meetingHref(n.meetingId, n.at)}>
            {n.frame.kind === "frame"
              ? <span className="p-frame p-neighbor__frame" style={{ backgroundImage: `url("${n.frame.url}")` }} role="img" aria-label={`Video frame of ${n.name}`}><span className="p-frame__tag"><Film size={9} aria-hidden />{mdy(n.date)}</span></span>
              : <span className="p-frame p-frame--none p-neighbor__frame" aria-hidden />}
            <span className="p-neighbor__text">
              <b>{n.name}</b>
              <small>{n.areas.join(" · ")}</small>
              <LeaningMeter estimate={n.leaning} size="mini" />
            </span>
          </a>
        ))}
      </div>
    </section>
  );
}

function Acted({ top }: { top: ResTop }) {
  const [standing, setStanding] = useState<string | null>(null);
  return (
    <div className="p-yours__acted">
      <span className="p-eyebrow p-yours__eyebrow">HOW THEY ACTED ON WHAT YOU FOLLOW</span>
      {top.acted.map((x) => (
        <div key={x.label} className="p-acted">
          <i className="type-badge" data-type={x.type} aria-hidden>{x.type === "topic" ? <Tag size={11} /> : <ScrollText size={11} />}</i>
          <span className="p-acted__text">
            <button className="p-acted__label" onClick={() => setStanding(standing === x.label ? null : x.label)} aria-expanded={standing === x.label}>{x.label}</button>
            <small>{x.sub}</small>
            {standing === x.label && x.matterId && <StanceWidget matterId={x.matterId} label={x.label} />}
          </span>
          {x.stance === "none" ? <span className="chip chip--neutral p-chip-sm">No position yet</span> : <StanceChip stance={x.stance} />}
          {x.meetingId != null && x.at != null && <Watch meetingId={x.meetingId} at={x.at} />}
        </div>
      ))}
    </div>
  );
}

function YouVs({ top }: { top: ResTop }) {
  const a = top.alderperson!;
  const you = top.you;
  const al = a.leaning?.status === "estimated" ? a.leaning : null;
  const yl = you?.leaning.status === "estimated" ? you.leaning : null;
  return (
    <div className="p-yours__vs">
      <span className="p-yours__eyebrow p-yours__eyebrow--chip"><span className="p-eyebrow">YOU VS. YOUR ALDERPERSON</span><span className="ai-chip"><Sparkles size={11} aria-hidden />AI estimate</span></span>
      <div className="leaning leaning--full p-vs-meter" role="img"
        aria-label={`AI estimates on one meter: ${a.name}${yl ? " and you" : ""}`}>
        <div className="leaning__track">
          {al && <span className="p-vs-face" style={{ left: `${leaningToPct(al.score)}%` }}><Face name={a.name} photoUrl={a.image.kind === "portrait" ? a.image.url : null} size={22} /></span>}
          {yl && <span className="p-vs-you" style={{ left: `${leaningToPct(yl.score)}%` }}><b>You</b></span>}
        </div>
        <div className="leaning__scale"><span>Progressive</span><span>Moderate</span><span>Conservative</span></div>
      </div>
      {you && (
        <p className="p-yours__note">
          Your estimate comes from your own {you.comments} public {you.comments === 1 ? "comment" : "comments"}{you.leaning.status === "estimated" ? ` (${you.leaning.confidence} confidence)` : ""}.{" "}
          {you.sharedItems ? `You agree on ${you.agreed} of ${you.sharedItems} shared items.` : "You haven't both taken a position on the same item yet."}
        </p>
      )}
    </div>
  );
}

/** Where do you stand? (card 5): a private Support / Oppose on an item. */
function StanceWidget({ matterId, label }: { matterId: string; label: string }) {
  const [pos, setPos] = useState<"support" | "oppose" | null>(null);
  const save = async (next: "support" | "oppose") => {
    const value = pos === next ? null : next;
    setPos(value);
    await peopleApi("stance", { method: "PUT", body: JSON.stringify({ matterId, position: value }) }).catch(() => setPos(pos));
  };
  return (
    <div className="p-stance-widget" role="group" aria-label={`Where do you stand on ${label}?`}>
      <div className="p-stance-widget__btns">
        <button aria-pressed={pos === "support"} onClick={() => save("support")}><ThumbsUp size={13} aria-hidden />Support</button>
        <button aria-pressed={pos === "oppose"} onClick={() => save("oppose")}><ThumbsDown size={13} aria-hidden />Oppose</button>
      </div>
      <small><Lock size={11} aria-hidden />Only you see your stances</small>
    </div>
  );
}

/** Find my alderperson (card 4): the address is located in your browser (OpenStreetMap, as on the public site); only the point is sent to us. */
function FindWard({ onDone }: { onDone: () => void }) {
  const [addr, setAddr] = useState("");
  const [hit, setHit] = useState<{ ward: number; alderperson: { id: string; name: string; photoUrl: string | null } | null } | null>(null);
  const [state, setState] = useState<"idle" | "busy" | "none">("idle");
  const find = async () => {
    if (!addr.trim()) return;
    setState("busy");
    try {
      const url = `https://nominatim.openstreetmap.org/search?format=json&limit=1&countrycodes=us&bounded=1&viewbox=-87.95,42.03,-87.52,41.64&q=${encodeURIComponent(`${addr}, Chicago, IL`)}`;
      const [g] = await (await fetch(url, { headers: { Accept: "application/json" } })).json();
      const r = g ? await peopleApi("ward", { method: "POST", body: JSON.stringify({ lat: +g.lat, lon: +g.lon }) }) : null;
      setHit(r?.ward ? r : null);
      setState(r?.ward ? "idle" : "none");
    } catch { setState("none"); }
  };
  const { run: runSave } = useBusy();
  const save = () => runSave(async () => {
    if (!hit) return;
    try { await peopleApi("ward", { method: "POST", body: JSON.stringify({ ward: hit.ward, save: true }) }); } catch { return; }
    onDone();
    reloadPage(); // the page's first paint is built for your ward: load it again, in place
  });
  return (
    <div className="p-findward" role="dialog" aria-label="Find my alderperson">
      <form onSubmit={(e) => { e.preventDefault(); find(); }}>
        <label className="p-findward__input"><MapPin size={14} aria-hidden /><input autoFocus aria-label="Street address" value={addr} onChange={(e) => { setAddr(e.target.value); setState("idle"); }} /></label>
      </form>
      {hit?.alderperson && (
        <div className="p-findward__hit">
          <Face name={hit.alderperson.name} photoUrl={hit.alderperson.photoUrl} size={28} />
          <span><b>Ward {hit.ward} · {hit.alderperson.name}</b><small>Matched from your address</small></span>
        </div>
      )}
      {hit && <button className="btn btn--primary p-findward__save" onClick={save}><Check size={14} aria-hidden />Set as my ward</button>}
    </div>
  );
}


/* ---------------- Lobbyist / advocacy (23) ---------------- */

type View = "all" | "persuadable" | "committees";

export function AdvocacyOverview({ canTarget, canBrief }: { canTarget: boolean; canBrief: boolean }) {
  const top = usePeople((s) => s.advocacy);
  const [view, setView] = useState<View>("all");
  const [editing, setEditing] = useState(false);
  const [allMakers, setAllMakers] = useState(false);
  const [allOrgs, setAllOrgs] = useState(false);
  const list = useRef<HTMLDivElement>(null);
  const flip = useRef<(() => void) | null>(null);
  // Re-rank in place (card 12): measure rows before the store change renders, animate after.
  useEffect(() => peopleStore.subscribe((s) => s.advocacy, () => { if (list.current) flip.current = flipFrom(list.current); }), []);
  useLayoutEffect(() => { flip.current?.(); flip.current = null; });
  if (!top) return null;
  const filtered = top.makers.filter((m) => view === "all" || (view === "persuadable" ? m.persuadable : m.onCommittee));
  const makers = allMakers ? filtered : filtered.slice(0, 5);
  const persuadable = top.makers.filter((m) => m.persuadable).length;
  return (
    <>
      <div className="p-positions" aria-label="Your team's positions">
        <span className="p-positions__label">Your team's positions</span>
        {top.positions.map((p) => (
          <span key={p.id} className="p-position"><Tag size={12} aria-hidden className="p-position__tag" />{p.label}<StanceChip stance={p.stance} /></span>
        ))}
        {top.canEditPositions && <button className="p-link p-positions__edit" onClick={() => setEditing(true)}><Pencil size={12} aria-hidden />Edit positions</button>}
      </div>
      {editing && <EditPositions positions={top.positions} onClose={() => setEditing(false)} />}
      <div className="p-adv">
        <section className="p-adv__main">
          <div className="p-adv__head">
            <h2>Decision makers on your issues</h2><CountBadge>{top.makers.length}</CountBadge>
            <div className="p-seg" role="radiogroup" aria-label="Decision makers">
              {([["all", "All"], ["persuadable", `Persuadable · ${persuadable}`], ["committees", "On your committees"]] as const).map(([id, label]) => (
                <button key={id} role="radio" aria-checked={view === id} onClick={() => setView(id)}>{label}</button>
              ))}
            </div>
          </div>
          <div className="p-dm" role="table" aria-label="Decision makers">
            <ListRow as="div" className="p-dm__row p-dm__row--head" role="row">
              <RowMain min={160}><span role="columnheader">OFFICIAL</span></RowMain>
              <RowCell width={170}><span role="columnheader">WHY THEY MATTER</span></RowCell>
              <RowCell width={150}><span role="columnheader">ALIGNMENT WITH YOU</span></RowCell>
              <RowCell width={130}><span role="columnheader">LEANING</span></RowCell>
              <RowCell width={90}><span role="columnheader" /></RowCell>
            </ListRow>
            <List as="div" variant="plain" className="p-dm__list" ref={list}>
              {makers.map((m) => <MakerRow key={m.person.id} m={m} targeted={top.targets.some((t) => t.personId === m.person.id)} canTarget={canTarget} />)}
            </List>
            <div className="p-dm__foot" role="row">
              <span role="cell">Showing {makers.length} of {filtered.length} decision makers on your issues</span>
              {!allMakers && filtered.length > makers.length && <span role="cell"><button className="link-arrow" onClick={() => setAllMakers(true)}>See all {filtered.length}<ArrowRight size={14} aria-hidden /></button></span>}
            </div>
          </div>
        </section>
        <aside className="p-adv__side">
          {canTarget && <TargetList top={top} canBrief={canBrief} />}
          <section className="p-card p-card--290">
            <h3>Coalition finder</h3>
            <p className="p-card__sub">Organizations that took the same side as you on the record</p>
            {(allOrgs ? top.coalition : top.coalition.slice(0, 3)).map((o) => (
              <div key={o.id} className="p-coalition">
                <span className="icon-tile"><Building2 size={14} aria-hidden /></span>
                <span><a href={`/dashboard/organizations/${encodeURIComponent(o.id)}`}>{o.name}</a><small>{o.sub}</small></span>
              </div>
            ))}
            {!allOrgs && top.coalition.length > 3 && <button className="link-arrow p-card__foot" onClick={() => setAllOrgs(true)}>See all coalition matches<ArrowRight size={14} aria-hidden /></button>}
          </section>
        </aside>
      </div>
    </>
  );
}

const MakerRow = memo(function MakerRow({ m, targeted, canTarget }: { m: DecisionMaker; targeted: boolean; canTarget: boolean }) {
  const p = m.person;
  const pct = m.alignment?.pct;
  const tone = pct == null ? "none" : pct >= 60 ? "ally" : pct >= 40 ? "mid" : "opponent";
  const add = async () => {
    await peopleApi(`${encodeURIComponent(p.id)}/target`, { method: "PUT", body: JSON.stringify({ status: "to_contact", owner: "me" }) });
    const a = peopleStore.getState().advocacy;
    if (a) peopleStore.getState().setAdvocacy({ ...a, targets: [...a.targets, { personId: p.id, name: p.name, photoUrl: p.image.kind === "portrait" ? p.image.url : null, status: "to_contact" }] });
  };
  return (
    <ListRow as="div" className="p-dm__row" role="row" data-flip-key={p.id}>
      <RowMain min={160}>
        <span className="p-dm__who" role="cell">
          <Face name={p.name} photoUrl={p.image.kind === "portrait" ? p.image.url : null} size={30} />
          <span>
            <RowTitle><a href={profileHref(p.id)}>{p.name}</a></RowTitle>
            <RowSub><small>Ward {p.ward}</small></RowSub>
            {m.persuadable && <span className="p-persuadable"><UserRound size={10} aria-hidden />Persuadable</span>}
          </span>
        </span>
      </RowMain>
      <RowCell width={170}><span role="cell" className="p-dm__why">{m.why}</span></RowCell>
      <RowCell width={150}><span role="cell" className="p-dm__align" data-tone={tone}>
        {m.alignment && <>
          <span><RollNumber value={m.alignment.pct} />%<small>of your positions</small></span>
          <span className="p-dm__bar"><span style={{ width: `${m.alignment.pct}%` }} /></span>
        </>}
      </span></RowCell>
      <RowCell width={130}><span role="cell"><LeaningMeter estimate={p.leaning} size="mini" /></span></RowCell>
      <RowCell width={90} align="end"><span role="cell" className="p-dm__act">
        {targeted ? <span className="p-targeted"><Check size={12} aria-hidden />Targeted</span>
          : canTarget && <button className="btn p-btn-sm" onClick={add}><Plus size={13} aria-hidden />Target</button>}
      </span></RowCell>
    </ListRow>
  );
});

/** A number that rolls to its new value when it changes after first paint (motion #11). */
function RollNumber({ value }: { value: number }) {
  const el = useRef<HTMLSpanElement>(null);
  const prev = useRef(value);
  useEffect(() => {
    const from = prev.current;
    prev.current = value;
    if (from === value || !el.current) return;
    if (prefersReducedMotion()) { el.current.textContent = String(value); return; }
    const start = performance.now(), d = recipes.liveValueChange.duration;
    const tick = (t: number) => {
      const k = Math.min(1, (t - start) / d);
      if (el.current) el.current.textContent = String(Math.round(from + (value - from) * k));
      if (k < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [value]);
  return <span ref={el}>{value}</span>;
}

const STATUS: Record<TargetStatus, string> = { to_contact: "To contact", scheduled: "Scheduled", met: "Met" };
const statusText = (t: Target) => (t.status === "met" ? `Met${t.date ? ` · ${mdy(t.date)}` : ""}` : t.status === "scheduled" ? `Meeting scheduled${t.date ? ` · ${mdy(t.date)}` : ""}` : "To contact");

function TargetList({ top, canBrief }: { top: AdvTop; canBrief: boolean }) {
  const [drag, setDrag] = useState<string | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [brief, setBrief] = useState(false);
  const save = (targets: Target[]) => {
    peopleStore.getState().setAdvocacy({ ...top, targets });
    peopleApi("targets/order", { method: "PUT", body: JSON.stringify({ ids: targets.map((t) => t.personId) }) }).catch(() => {});
  };
  const move = (id: string, to: number) => {
    const list = top.targets.filter((t) => t.personId !== id);
    list.splice(Math.max(0, Math.min(list.length, to)), 0, top.targets.find((t) => t.personId === id)!);
    save(list);
  };
  return (
    <section className="p-card p-card--290">
      <div className="p-card__head"><h3>Target list</h3><span className="p-team"><Users size={11} aria-hidden />Team</span></div>
      <ol className="p-targets">
        {top.targets.map((t, i) => (
          <li key={t.personId} draggable onDragStart={() => setDrag(t.personId)} onDragOver={(e) => e.preventDefault()}
            onDrop={() => { if (drag && drag !== t.personId) move(drag, i); setDrag(null); }}
            tabIndex={0} aria-label={`${t.name}, ${statusText(t)}. Arrow keys move it.`}
            onKeyDown={(e) => { if (e.key === "ArrowUp") { e.preventDefault(); move(t.personId, i - 1); } if (e.key === "ArrowDown") { e.preventDefault(); move(t.personId, i + 1); } }}>
            <GripVertical size={14} className="p-targets__grip" aria-hidden />
            <Face name={t.name} photoUrl={t.photoUrl} size={22} />
            <a href={profileHref(t.personId)}>{t.name}</a>
            <button className={`p-tstatus p-tstatus--${t.status}`} onClick={() => setEditing(editing === t.personId ? null : t.personId)}>{statusText(t)}</button>
            {editing === t.personId && <StatusPopover t={t} onClose={() => setEditing(null)} onSave={(next) => save(top.targets.map((x) => (x.personId === t.personId ? next : x)))} />}
          </li>
        ))}
      </ol>
      {canBrief && <button className="btn btn--block p-brief-btn" onClick={() => setBrief(true)}><FileText size={14} aria-hidden />Brief me before a meeting</button>}
      {brief && <Brief targets={top.targets} onClose={() => setBrief(false)} />}
    </section>
  );
}

/** Card 10: status chips and the owner. */
function StatusPopover({ t, onClose, onSave }: { t: Target; onClose: () => void; onSave: (t: Target) => void }) {
  const [status, setStatus] = useState(t.status);
  const [date, setDate] = useState(t.date ?? "");
  const commit = async () => {
    await peopleApi(`${encodeURIComponent(t.personId)}/target`, { method: "PUT", body: JSON.stringify({ status, date: status === "to_contact" ? null : date || null }) });
    onSave({ ...t, status, date: status === "to_contact" ? undefined : date || undefined });
    onClose();
  };
  return (
    <div className="p-pop p-tpop" role="dialog" aria-label={`Status for ${t.name}`}>
      <span className="p-eyebrow">STATUS</span>
      <div className="p-tpop__chips">
        {(Object.keys(STATUS) as TargetStatus[]).map((s) => <button key={s} aria-pressed={status === s} onClick={() => setStatus(s)}>{STATUS[s]}</button>)}
      </div>
      {status !== "to_contact" && <input type="date" aria-label="Date" value={date} onChange={(e) => setDate(e.target.value)} />}
      <span className="p-tpop__owner"><UserRound size={13} aria-hidden />Owner: {t.owner ?? "you"}</span>
      <div className="p-tpop__actions">
        <button className="btn" onClick={async () => { await peopleApi(`${encodeURIComponent(t.personId)}/target`, { method: "DELETE" }); const a = peopleStore.getState().advocacy; if (a) peopleStore.getState().setAdvocacy({ ...a, targets: a.targets.filter((x) => x.personId !== t.personId) }); onClose(); }}><X size={13} aria-hidden /></button>
        <button className="btn btn--primary" onClick={commit}><Check size={13} aria-hidden /></button>
      </div>
    </div>
  );
}

interface BriefData { person: { id: string; name: string }; alignment: string; said: { topic: string; quote: string; at: number }; echoedBy: { name: string; at: number } | null }

/** Brief me (card 11): an AI draft one-page brief, from the member's recorded positions, quotes and exchanges. */
function Brief({ targets, onClose }: { targets: Target[]; onClose: () => void }) {
  const [who, setWho] = useState(targets.find((t) => t.status === "scheduled")?.personId ?? targets[0]?.personId ?? "");
  const [data, setData] = useState<BriefData | null>(null);
  const [busy, setBusy] = useState(false);
  const [limit, setLimit] = useState<BriefAllowance | null>(null);   // the plan's briefs a month are used up (k9qobd #2)
  const box = useRef<HTMLDivElement>(null);
  useFocusTrap(box, true, { onEscape: onClose });
  const make = async (id: string) => {
    setBusy(true);
    const r = await fetch(`/api/dashboard/people/${encodeURIComponent(id)}/brief`, { method: "POST", credentials: "include" }).catch(() => null);
    const json = r ? await r.json().catch(() => null) : null;
    const reached = limitAnswer(json);
    setData(r?.ok ? (json as BriefData) : null);
    setLimit(reached?.code === "limit_reached" ? reached.briefs : null);
    setBusy(false);
  };
  useEffect(() => { if (who) make(who); }, [who]);
  const share = () => data && navigator.clipboard?.writeText(`Brief · ${data.person.name}\nAlignment with you: ${data.alignment}\nSaid on ${data.said.topic}: “${data.said.quote}” ${clock(data.said.at)}${data.echoedBy ? `\nEchoed by ${data.echoedBy.name} · ${clock(data.echoedBy.at)}` : ""}`);
  return (
    <div className="p-dialog-backdrop" onClick={onClose}>
      <div className="p-brief" role="dialog" aria-modal="true" aria-label="Brief" ref={box} onClick={(e) => e.stopPropagation()} aria-busy={busy}>
        <div className="p-brief__head">
          <Dropdown label="Who" size="sm" value={who} onChange={setWho} options={targets.map((t) => ({ value: t.personId, label: t.name }))} />
          <span className="ai-chip"><Sparkles size={11} aria-hidden />AI draft</span>
        </div>
        {briefsReached(limit) && <BriefLimitCard allowance={limit} role={viewerRole()} onDismiss={onClose} />}
        {data && <>
          <h3>Brief · {data.person.name}</h3>
          <div className="p-brief__sec"><span>Alignment with you</span><p>{data.alignment}</p></div>
          <div className="p-brief__sec"><span>Said on {data.said.topic}</span><p>“{data.said.quote}” {clock(data.said.at)}</p></div>
          {data.echoedBy && <div className="p-brief__sec"><span>Echoed by</span><p>{data.echoedBy.name} · {clock(data.echoedBy.at)}</p></div>}
          <div className="p-brief__actions">
            <button className="btn" onClick={share}><Share2 size={14} aria-hidden />Share with team</button>
            <button className="btn btn--primary" onClick={() => print()}><Printer size={14} aria-hidden />PDF</button>
          </div>
        </>}
      </div>
    </div>
  );
}

/** Edit positions (owner/admin): stance flips, remove, add. Saving re-ranks the decision makers in place. */
function EditPositions({ positions, onClose }: { positions: TeamPosition[]; onClose: () => void }) {
  const [list, setList] = useState(positions);
  const box = useRef<HTMLDivElement>(null);
  useFocusTrap(box, true, { onEscape: onClose });
  const [label, setLabel] = useState("");
  const [ref, setRef] = useState("");
  const add = () => {
    if (!label.trim()) return;
    const isRecord = /^[A-Z]{1,3}\d{4}-\d{5,}$/i.test(ref.trim());
    setList([...list, { id: crypto.randomUUID(), label: label.trim(), stance: "supports", ...(isRecord ? { recordId: ref.trim().toUpperCase() } : ref.trim() ? { topic: ref.trim() } : { topic: label.trim() }) }]);
    setLabel(""); setRef("");
  };
  return (
    <div className="p-dialog-backdrop" onClick={onClose}>
      <div className="p-editpos" role="dialog" aria-modal="true" aria-label="Your team's positions" ref={box} onClick={(e) => e.stopPropagation()}>
        <h3>Your team's positions</h3>
        {list.map((p) => (
          <div key={p.id} className="p-editpos__row">
            <span>{p.label}<small>{p.recordId ?? p.topic}</small></span>
            <button onClick={() => setList(list.map((x) => (x.id === p.id ? { ...x, stance: x.stance === "supports" ? "opposes" : "supports" } : x)))}><StanceChip stance={p.stance} /></button>
            <button aria-label={`Remove ${p.label}`} onClick={() => setList(list.filter((x) => x.id !== p.id))}><X size={14} /></button>
          </div>
        ))}
        <form className="p-editpos__add" onSubmit={(e) => { e.preventDefault(); add(); }}>
          <input aria-label="Position" value={label} onChange={(e) => setLabel(e.target.value)} />
          <input aria-label="Record number or topic" value={ref} onChange={(e) => setRef(e.target.value)} />
          <button className="btn" type="submit"><Plus size={13} aria-hidden /></button>
        </form>
        <div className="p-brief__actions">
          <button className="btn" onClick={onClose}><X size={13} aria-hidden /></button>
          <button className="btn btn--primary" onClick={() => { peopleStore.getState().savePositions(list); onClose(); }}><Check size={13} aria-hidden /></button>
        </div>
      </div>
    </div>
  );
}
